import { PermissionScope, useRolePermission } from '@/components/RolePermission'
import { USER_ROLE } from '@/constant/user'
import { useCallback } from 'react'
import type { Product, ProductDetail } from './ProductTransform'

type ProductPermissionUser = {
  id?: string
  role?: USER_ROLE | string
  role_access?: {
    role?: USER_ROLE | string
  }
  subscription?: {
    role_access?: {
      role?: USER_ROLE | string
    }
  }
  subscriptions?: Array<{
    role_access?: {
      role?: USER_ROLE | string
    }
  }>
  suppliers?: Array<{
    id?: string
  }>
}

type ProductPermissionTarget = Product | ProductDetail | null | undefined

export const PRODUCT_PERMISSION_MODULE = 'products'

export function getProductUserRole(user?: ProductPermissionUser) {
  const role =
    user?.subscription?.role_access?.role ??
    user?.role_access?.role ??
    user?.subscriptions?.[0]?.role_access?.role ??
    user?.role

  return role?.trim().toUpperCase()
}

function getUserSupplierIds(user?: ProductPermissionUser) {
  return new Set(
    user?.suppliers?.map((supplier) => supplier.id).filter(Boolean)
  )
}

function isProductForUserSupplier(
  user: ProductPermissionUser | undefined,
  product: ProductPermissionTarget
) {
  const supplierIds = getUserSupplierIds(user)

  if (!supplierIds.size || !product) return false

  return (
    supplierIds.has(product.supplier?.id) ||
    supplierIds.has(product.factory?.id)
  )
}

function isProductCreatedByUser(
  user: ProductPermissionUser | undefined,
  product: ProductPermissionTarget
) {
  return Boolean(user?.id && product?.created_by?.id === user.id)
}

function getProductResourceScopes(
  user: ProductPermissionUser | undefined,
  product: ProductPermissionTarget
) {
  const scopes: PermissionScope[] = []

  if (isProductCreatedByUser(user, product)) {
    scopes.push('own')
  }

  if (isProductForUserSupplier(user, product)) {
    scopes.push('assigned')
  }

  return scopes
}

export function useProductPermission(user?: ProductPermissionUser) {
  const { can, isLoading } = useRolePermission()
  const isAdmin = getProductUserRole(user) === USER_ROLE.ADMIN
  const canAccessAction = useCallback(
    (action: 'create' | 'read' | 'update' | 'delete' | 'export') =>
      isAdmin ||
      can({
        action,
        module: PRODUCT_PERMISSION_MODULE,
      }),
    [can, isAdmin]
  )
  const canAccessProduct = useCallback(
    (action: 'read' | 'update' | 'delete', product: ProductPermissionTarget) =>
      isAdmin ||
      can({
        action,
        module: PRODUCT_PERMISSION_MODULE,
        resourceScopes: getProductResourceScopes(user, product),
      }),
    [can, isAdmin, user]
  )

  return {
    canCreateProduct: useCallback(
      () => canAccessAction('create'),
      [canAccessAction]
    ),
    canDeleteProduct: useCallback(
      (product: ProductPermissionTarget) => canAccessProduct('delete', product),
      [canAccessProduct]
    ),
    canEditProduct: useCallback(
      (product: ProductPermissionTarget) => canAccessProduct('update', product),
      [canAccessProduct]
    ),
    canExportProducts: useCallback(
      () => canAccessAction('export'),
      [canAccessAction]
    ),
    canViewProduct: useCallback(
      (product: ProductPermissionTarget) => canAccessProduct('read', product),
      [canAccessProduct]
    ),
    isLoading: !isAdmin && isLoading,
  }
}

export function isProductCreatedBySupplier(product: ProductPermissionTarget) {
  const createdBy = product?.created_by as
    | {
        subscription?: {
          role_access?: {
            role?: string
          }
        } | null
        subscriptions?: Array<{
          role_access?: {
            role?: string
          }
        }>
        role?: string
        role_access?: {
          role?: string
        }
      }
    | undefined
  const creatorRole =
    createdBy?.role ||
    createdBy?.role_access?.role ||
    createdBy?.subscription?.role_access?.role ||
    createdBy?.subscriptions?.[0]?.role_access?.role
  const productSubscriptionRole = (product as ProductDetail | undefined)
    ?.subscription?.role_access?.role
  const productMisspelledSubscriptionRole = (
    product as ProductDetail | undefined
  )?.subsciription?.role_access?.role
  const supplierRole = USER_ROLE.SUPPLIER.toLowerCase()

  return (
    creatorRole?.toLowerCase() === supplierRole ||
    productSubscriptionRole?.toLowerCase() === supplierRole ||
    productMisspelledSubscriptionRole?.toLowerCase() === supplierRole
  )
}
